import type { Messages } from "../types";

export const messages: Messages = {
  shared: {
    title: "Konto klienta Fielmann",
    tabs: {
      login: "Zaloguj się",
      signup: "Utwórz konto",
      ariaLabel: "Uwierzytelnienie",
    },
    languageSwitcher: {
      label: "Wybierz język",
    },
    password: {
      show: "Pokaż hasło",
      hide: "Ukryj hasło",
    },
    header: {
      logoAriaLabel: "Fielmann",
    },
    footer: {
      ariaLabel: "Stopka",
      customerService: "Obsługa klienta",
      legalNotice: "Impressum",
      dataProtection: "Ochrona danych",
    },
    loading: {
      ariaLabel: "Jeszcze chwilę...",
      content: "Trwa ładowanie danych.",
    },
    genericError: "Wystąpił błąd. Spróbuj ponownie później.",
  },
  login: {
    subtitle: "Zaloguj się za pomocą swojego adresu e-mail i hasła.",
    form: {
      emailLabel: "Adres e-mail *",
      passwordLabel: "Hasło *",
      forgotPassword: "Zapomniałeś hasła?",
      submitButton: "Zaloguj się",
      credentialError: "Adres e-mail i hasło nie pasują do siebie.",
    },
  },
  signup: {
    subtitle: {
      before: "Skorzystaj z ",
      highlight: "dodatkowych możliwości",
      after: " i załóż konto klienta Fielmann.",
    },
    benefits: {
      popoverHeading: "Twoje korzyści:",
      closeLabel: "Zamknij",
      items: {
        orders:
          "Wszystkie zamówienia Fielmann wraz z ich statusem w jednym miejscu.",
        visionValues: "Wykorzystaj parametry wzroku przy następnym zamówieniu.",
        appointments: "Umawiaj wizyty i zarządzaj nimi.",
        reorders: "Łatwe i szybkie składanie ponownych zamówień online.",
      },
    },
    form: {
      firstNameLabel: "Imię *",
      lastNameLabel: "Nazwisko *",
      emailLabel: "Adres e-mail *",
      passwordLabel: "Hasło *",
      passwordRepeatLabel: "Powtórz hasło *",
      submitButton: "Utwórz konto",
      errorTitle: "Nie udało się utworzyć konta.",
      errorMessage: "Sprawdź wprowadzone dane lub spróbuj ponownie później.",
    },
    consents: {
      requiredError: "Zaakceptuj wymaganą zgodę.",
      unavailableError: "Wystąpił błąd. Spróbuj ponownie później.",
      required: {
        headline: "Zgoda na konto klienta",
        subline:
          "Aby wyświetlać wszystkie treści na twoim koncie klienta (np. zakupy lub wizyty), potrzebujemy twojej zgody.",
      },
      optional: {
        headline: "Czy chcesz otrzymywać spersonalizowane reklamy?",
        subline:
          "Masz wybór: wyrażając zgodę, otrzymujesz dopasowane oferty i informacje. Bez zgody korzystasz z konta klienta bez spersonalizowanych reklam.",
      },
    },
  },
  emailVerification: {
    subtitle: "Nie możesz się zalogować?",
    alertTitle: "Najpierw potwierdź swój adres e-mail.",
    alertBody:
      "Najpierw potwierdź swój adres e-mail, aby aktywować konto Fielmann.",
    alertSpamHint: "Nie możesz znaleźć wiadomości e-mail? Sprawdź folder spam",
    resendButton: "Poproś o ponowne przesłanie wiadomości e-mail",
    backToLogin: "Wróć do logowania",
  },
  resetPasswordRequest: {
    subtitle: "Nie pamiętasz hasła?",
    body: "Podaj adres e-mail, użyty podczas tworzenia konta. Wyślemy Ci link, za pomocą którego zresetujesz hasło.",
    emailLabel: "Adres e-mail *",
    submitButton: "Poproś o link",
    backToLogin: "Wróć do logowania",
    banner: {
      closeAria: "Zamknij",
      rateLimit: {
        title: "Zbyt wiele próśb o wiadomość e-mail",
        body: "Sprawdź swoją skrzynkę odbiorczą i folder spam, aby znaleźć wiadomość e-mail do zresetowania hasła.",
        waitHint:
          "Nadal nie widzisz wiadomości? Proszę, poczekaj chwilę – wiadomość e-mail może jeszcze dotrzeć.",
      },
      error: {
        title: "Wysyłanie nie powiodło się",
        body: "Spróbuj ponownie później.",
      },
    },
  },
  resetPasswordEmail: {
    success: {
      heading: "Wysłano wiadomość e-mail do zresetowania hasła.",
      body: "Wysłano wiadomość e-mail z prośbą o zresetowanie hasła. Sprawdź swoją skrzynkę odbiorczą.",
      spamHint: "Nie możesz znaleźć wiadomości e-mail? Sprawdź folder spam.",
    },
    restart: {
      hint: "Jeśli nadal nie możesz znaleźć wiadomości e-mail, możesz rozpocząć proces ponownie. Być może użyto innego adresu e-mail.",
      button: "Spróbuj ponownie",
    },
    error: {
      heading: "Wysyłanie nie powiodło się",
      body: "Spróbuj ponownie później.",
      retryButton: "Spróbuj jeszcze raz",
      rateLimitBody:
        "Zbyt wiele prób. Odczekaj kilka minut, zanim spróbujesz ponownie.",
    },
  },
  setPassword: {
    subtitle: "Teraz możesz ustawić hasło.",
    passwordLabel: "Hasło *",
    passwordRepeatLabel: "Powtórz hasło *",
    submitButton: "Zapisz hasło",
    backToLogin: "Przejdź do logowania",
    successTitle: "Twoje hasło zostało pomyślnie zapisane.",
    successRedirectBody:
      "Za chwilę nastąpi przekierowanie na naszą stronę główną.",
    successRedirectLoginHint:
      "Tam możesz zalogować się przy użyciu nowego hasła.",
    successWaitHint: "Chwileczkę.",
  },
  resetPasswordError: {
    expired: {
      heading: "Link wygasł.",
      body: "Link do zresetowania hasła stracił ważność. Wróć na stronę główną i rozpocznij proces ponownie, wybierając opcję „Zapomniałeś hasła?” podczas logowania.",
    },
    used: {
      heading: "Ten link został już użyty.",
      body: "Hasło zostało już zresetowane za pomocą tego linku. Aby ponownie zmienić hasło, wróć na stronę główną i rozpocznij proces od nowa, wybierając opcję „Zapomniałeś hasła?” podczas logowania.",
    },
    generic: {
      heading: "Link jest nieprawidłowy.",
      body: "Skorzystanie z linku nie było możliwe. Wróć na stronę główną i rozpocznij proces ponownie, wybierając opcję „Zapomniałeś hasła?” podczas logowania.",
    },
    backToHome: "Powrót do strony głównej",
  },
  toasts: {
    missingRequiredFields:
      "Wypełnij wszystkie jeszcze nie wypełnione pola obowiązkowe, aby kontynuować.",
  },
  validation: {
    passwordRequired: "Wpisz hasło.",
    passwordRepeatRequired: "Wpisz hasło ponownie.",
    firstNameTooLong: "Imię nie może być dłuższe niż 30 znaków.",
    firstNameRequired: "Podaj imię.",
    firstNameInvalidFormat: "Podaj prawidłowe imię.",
    lastNameTooLong: "Nazwisko nie może być dłuższe niż 30 znaków.",
    lastNameRequired: "Podaj nazwisko.",
    lastNameInvalidFormat: "Podaj prawidłowe nazwisko.",
    emailTooLong: "Adres e-mail jest za długi.",
    emailRequired: "Podaj adres e-mail.",
    emailInvalidFormat: "Wpisz prawidłowy adres e-mail.",
    passwordMatchesEmail: "Hasło nie może być identyczne z adresem e-mail.",
    passwordMismatch: "Hasła nie są zgodne.",
  },
  passwordRequirements: {
    minimumLength: "Co najmniej 10 znaków",
    mixedCase: "Co najmniej jedna wielka i jedna mała litera",
    hasNumber: "Co najmniej jedna cyfra",
    specialCharactersAllowed: "Znaki specjalne są dozwolone (np. !@#$%^&*)",
  },
};
